import { ReactNode } from "react";
import { HeroUIProvider, ToastProvider } from "@heroui/react";
import { DynamicContextProvider } from "@dynamic-labs/sdk-react-core";
import { QueryClientProvider } from "@tanstack/react-query";
import { SolanaWalletConnectors } from "@dynamic-labs/solana";
import { ThemeProvider } from "next-themes";
import { Provider as JotaiProvider, useSetAtom } from "jotai";

import { Auth } from "../lib/constants";
import {
  onboardingProgressState,
  onboardingStepState,
  queryClient,
  store,
} from "../store";

interface ProvidersProps {
  children: ReactNode;
}

// Base UI providers (styling, themes, etc)
const UIProviders = ({ children }: ProvidersProps) => {
  return (
    <HeroUIProvider>
      <ToastProvider
        maxVisibleToasts={3}
        placement="bottom-right"
        disableAnimation={false}
        toastOffset={20}
        toastProps={{}}
      />
      {children}
    </HeroUIProvider>
  );
};

// Authentication related providers
const AuthProviders = ({ children }: ProvidersProps) => {
  const setOnboardingProgressState = useSetAtom(onboardingProgressState);
  const setOnboardingStepState = useSetAtom(onboardingStepState);

  return (
    <DynamicContextProvider
      locale={{
        en: {
          dyn_login: {
            title: {
              all: "Create Account/Login",
            },
          },
        },
      }}
      theme="dark"
      settings={{
        environmentId: Auth.environmentId,
        walletConnectors: [SolanaWalletConnectors],
        handlers: {
          // handleAuthenticatedUser: async ({ user }) => {
          // },
        },
        events: {
          onAuthSuccess: () => {
            console.log("onboarding user ...");
            setOnboardingStepState(0);
            setOnboardingProgressState(true);
          },
          onLogout: async () => {},
        },
      }}
    >
      {children}
    </DynamicContextProvider>
  );
};

// Add new provider group
const DataProviders = ({ children }: ProvidersProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <JotaiProvider store={store}>{children}</JotaiProvider>
    </QueryClientProvider>
  );
};

// Combine all providers with correct hierarchy
export const Providers = ({ children }: ProvidersProps) => {
  return (
    <UIProviders>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem={true}
        storageKey="mash-theme"
        forcedTheme="dark"
        themes={["dark"]}
      >
        <DataProviders>
          <AuthProviders>{children}</AuthProviders>
        </DataProviders>
      </ThemeProvider>
    </UIProviders>
  );
};
